import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';

import { iif, Observable, of } from 'rxjs';
import { debounceTime, filter, tap, takeUntil, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';

import { IAuroraState } from '@apps/aurora.state';
import * as fromIts2Store from '@apps/its2/store';
import * as fromRootStore from '@apps/root/store';
import { CreateService } from '@apps/its2/models/create-service';
import { UploadFilesComponent } from '@customer/itsm/upload-files';
import { NextParams, StepState } from '@ui/misc/wizard/models';
import { CustomFile } from '@common/models/custom-file';
import { Step3 } from '@apps/its2/models/create-service';

import { CreateServiceBase } from '../common/create-service-base';

@Component({
    selector: 'aa-create-service-step3',
    templateUrl: './create-service-step3.component.html',
    styleUrls: [
        './create-service-step3.component.scss',
        '../../request-create.scss'
    ],
    standalone: false
})
export class CreateServiceStep3Component extends CreateServiceBase implements OnInit
{
    private static stepIdx: number = 2;
    @ViewChild('filesToUpload', { static: true }) filesToUpload: UploadFilesComponent;
    public stepForm: UntypedFormGroup;
    public readonly messageField: string = 'message';
    public readonly fileToUploadField: string = 'fileUpload';
    private customFiles: CustomFile[] = [];
    private step3: Step3;

    constructor (
        translateService: TranslateService,
        private store: Store<IAuroraState>,
        private actions: Actions
    )
    {
        super(translateService);

        this.step3 = new Step3(translateService.currentLang);
        this.store.select(fromIts2Store.getCreateService)
            .pipe(
                take(1),
                tap((cs: CreateService) =>
                {
                    this.step3 = new Step3(translateService.currentLang, cs.step3);
                    this.createForm();
                })
            )
            .subscribe();
    }

    ngOnInit ()
    {
        this.actions
            .pipe(
                ofType(fromRootStore.RootMiscStoreActions.RootWizard2StepExitRequested),
                filter((nextParams: NextParams) =>
                    nextParams.oldStepIdx === CreateServiceStep3Component.stepIdx
                ),
                switchMap((nextParams: NextParams) =>
                    this.onSubmit()
                        .pipe(
                            switchMap((isValid: boolean) =>
                                iif(
                                    () => isValid,
                                    of(true)
                                        .pipe(
                                            tap(() =>
                                            {
                                                this.store.dispatch(
                                                    fromIts2Store.Its2MiscActions.Its2RequestsServiceCreateStep3Exited({ step3: this.step3 })
                                                );
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepState(
                                                        new StepState(CreateServiceStep3Component.stepIdx, true)
                                                    )
                                                );
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepExitAccepted(nextParams)
                                                );
                                            })
                                        ),
                                    of(false)
                                        .pipe(
                                            tap(() =>
                                            {
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepState(
                                                        new StepState(CreateServiceStep3Component.stepIdx, false)
                                                    )
                                                );
                                                this.store.dispatch(
                                                    fromRootStore.RootMiscStoreActions.RootWizard2StepExitRefused(nextParams)
                                                );
                                            })
                                        )
                                )
                            )
                        )
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    onSubmit = (): Observable<boolean> =>
    {
        this.markControlsDirty(this.stepForm);
        if (this.stepForm.invalid)
        {
            return of(false);
        }

        this.step3.valid = true;
        this.step3.message = this.stepForm.get(this.messageField).value;

        return of(true);
    };

    onFilesChange (evt: CustomFile[]): void
    {
        this.customFiles = evt;
    }

    private createForm ()
    {
        this.stepForm = new UntypedFormGroup({
            [this.messageField]: new UntypedFormControl('', Validators.required),
            [this.fileToUploadField]: new UntypedFormControl('')
        });
        this.stepForm.get(this.messageField).setValue(this.step3.message);
        this.formListener();
    }

    private formListener ()
    {
        this.stepForm.valueChanges
            .pipe(
                debounceTime(250),
                tap(() =>
                    this.store.dispatch(
                        fromRootStore.RootMiscStoreActions.RootDirtyPopinFormSet({ dirty: true })
                    )
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
        this.stepForm.statusChanges
            .pipe(
                debounceTime(250),
                tap((result: any) =>
                {
                    if (result === 'VALID')
                    {
                        this.store.dispatch(
                            fromRootStore.RootMiscStoreActions.RootWizard2StepState(
                                new StepState(CreateServiceStep3Component.stepIdx, true)
                            )
                        );
                    }
                    else
                    {
                        // INVALID, PENDING, or DISABLED
                        this.store.dispatch(
                            fromRootStore.RootMiscStoreActions.RootWizard2StepState(
                                new StepState(CreateServiceStep3Component.stepIdx, false)
                            )
                        );
                    }
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }
}
